import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Button } from "../ui/Button";
import { Mascot } from "../ui/Mascot";
import { useDialog } from "../ui/useDialog";

/**
 * The branded "are you sure?" that replaces the browser's own `confirm()`.
 *
 * It is asked for as a promise — `if (await confirm(...))` — so the action that needs it reads
 * top to bottom instead of being split into an "open" and a callback stored in state.
 */

export interface ConfirmRequest {
  title: string;
  body: string;
  label: string;
}

type Confirm = (request: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm needs a <ConfirmProvider> above it");
  return confirm;
}

interface Pending extends ConfirmRequest {
  resolve: (accepted: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<Confirm>(
    (request) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...request, resolve });
      }),
    [],
  );

  const settle = (accepted: boolean) => {
    pending?.resolve(accepted);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? <ConfirmDialog request={pending} onSettle={settle} /> : null}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ request, onSettle }: { request: ConfirmRequest; onSettle: (accepted: boolean) => void }) {
  const busy = useIsMutating() > 0;
  const ref = useDialog<HTMLDivElement>(() => onSettle(false), { initialFocus: ".cs-confirm-primary" });

  return (
    <div className="cs-confirm-scrim" role="presentation" onClick={() => onSettle(false)}>
      <div
        ref={ref}
        className="cs-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-body"
        onClick={(event) => event.stopPropagation()}
      >
        <Mascot mood="worried" size={84} />
        <div>
          <h2 id="confirm-dialog-title">{request.title}</h2>
          <p id="confirm-dialog-body">{request.body}</p>
        </div>
        <div className="cs-confirm-actions">
          <Button variant="secondary" onClick={() => onSettle(false)}>
            Cancelar
          </Button>
          <Button variant="danger" className="cs-confirm-primary" disabled={busy} onClick={() => onSettle(true)}>
            {request.label}
          </Button>
        </div>
      </div>
    </div>
  );
}
